import { fill, t } from '../strings';

/**
 * 🔒 A company that is not Active starts no new accounting document (decision 7530; the owner, 2026-10-24: *company status
 * has to be active also for new transaction in accounting*). The Company's `status` is Active, Bought Out or Out of Business
 * (piece 10); the two that are not Active are named here in the words the Company page draws.
 */
export const INACTIVE_COMPANY_STATUS_WORDS: Readonly<Record<string, string>> = {
  BoughtOut: 'Bought Out',
  OutOfBusiness: 'Out of Business',
};

/** The part of a Company the refusals read: its name and its stored status. */
export interface CompanyStanding {
  displayName: string;
  status: string;
}

/** The status in words where the company is not Active; null for an Active company and for a status this module does not name. */
export function inactiveStatusWords(status: string): string | null {
  return INACTIVE_COMPANY_STATUS_WORDS[status] ?? null;
}

/**
 * 🔒 The one refusal of a new document that names a company not Active, in a sentence naming the status (decision 7530):
 * *Northgate Systems is Bought Out, so a new document cannot name it.* Null where the company is Active.
 */
export function newDocumentCompanyRefusal(company: CompanyStanding): string | null {
  const words = inactiveStatusWords(company.status);
  if (!words) return null;
  return fill(t('company.inactive.newDocument'), { company: company.displayName, status: words });
}

/** The acts a document is held from while its company is not Active: sent, approved, issued, posted or submitted. */
export type CompanyHoldAct = 'sent' | 'approved' | 'issued' | 'posted' | 'submitted';

/** How the refusal names each kind of document, in the lower case a sentence takes. */
const HELD_DOCUMENT_NOUN: Readonly<Record<string, string>> = {
  Quote: 'quote',
  SalesOrder: 'sales order',
  ChangeOrder: 'change order',
  Invoice: 'invoice',
  PurchaseOrder: 'purchase order',
  Bill: 'bill',
  VendorCredit: 'vendor credit',
  ExpenseReport: 'expense report',
};

/** Whether a document type is one the hold reads. */
export function isCompanyHeldDocument(documentType: string): boolean {
  return documentType in HELD_DOCUMENT_NOUN;
}

/**
 * 🔒 The refusal of a send, an approval, an issue, a submit or a post while the document's company is not Active
 * (decision 7530): *Northgate Systems is Bought Out, so this invoice cannot be issued.* Null where the company is Active.
 */
export function companyHoldSentence(company: CompanyStanding, documentType: string, act: CompanyHoldAct): string | null {
  const words = inactiveStatusWords(company.status);
  if (!words) return null;
  const noun = HELD_DOCUMENT_NOUN[documentType] ?? 'document';
  return fill(t('company.inactive.hold'), { company: company.displayName, status: words, document: noun, act });
}

/** The query parameter a picker on a new document adds so the company list leaves out a company that is not Active (decision 7530). */
export const ACTIVE_COMPANIES_ONLY_QUERY = 'activeOnly=true';
